import { useRef, useCallback, useEffect } from 'react';

interface UsePanelScrollOptions {
  panelId: string;
  enableScrollRestoration?: boolean;
  enableScrollToTop?: boolean;
}

interface ScrollPosition {
  scrollTop: number;
  scrollLeft: number;
}

export const usePanelScroll = ({ 
  panelId, 
  enableScrollRestoration = true, 
  enableScrollToTop = true 
}: UsePanelScrollOptions) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<ScrollPosition>({ scrollTop: 0, scrollLeft: 0 });

  // Save scroll position before unmounting
  const saveScrollPosition = useCallback(() => {
    if (scrollRef.current) {
      scrollPositionRef.current = {
        scrollTop: scrollRef.current.scrollTop,
        scrollLeft: scrollRef.current.scrollLeft
      };
      
      // Save to sessionStorage for persistence across page reloads
      if (enableScrollRestoration) {
        sessionStorage.setItem(
          `panel-scroll-${panelId}`, 
          JSON.stringify(scrollPositionRef.current)
        );
      }
    }
  }, [panelId, enableScrollRestoration]);

  // Restore scroll position
  const restoreScrollPosition = useCallback(() => {
    if (scrollRef.current && enableScrollRestoration) {
      const saved = sessionStorage.getItem(`panel-scroll-${panelId}`);
      if (saved) {
        try {
          const position: ScrollPosition = JSON.parse(saved);
          scrollRef.current.scrollTop = position.scrollTop;
          scrollRef.current.scrollLeft = position.scrollLeft;
        } catch (error) {
          console.warn('Failed to restore scroll position:', error);
        }
      }
    }
  }, [panelId, enableScrollRestoration]);

  // Scroll to top
  const scrollToTop = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll to element
  const scrollToElement = useCallback((elementId: string) => {
    if (scrollRef.current) {
      const element = scrollRef.current.querySelector(`[data-id="${elementId}"]`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, []);

  // Check if scrolled to top
  const isAtTop = useCallback(() => {
    return scrollRef.current ? scrollRef.current.scrollTop === 0 : true;
  }, []);

  // Check if scrolled to bottom
  const isAtBottom = useCallback(() => {
    if (!scrollRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    return Math.abs(scrollTop + clientHeight - scrollHeight) < 1;
  }, []);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      scrollPositionRef.current = {
        scrollTop: scrollRef.current.scrollTop,
        scrollLeft: scrollRef.current.scrollLeft
      };
    }
  }, []);

  // Set up scroll event listener
  useEffect(() => {
    const element = scrollRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll, { passive: true });
      
      // Restore position after a short delay to ensure content is loaded
      const timer = setTimeout(restoreScrollPosition, 100);
      
      return () => {
        element.removeEventListener('scroll', handleScroll);
        clearTimeout(timer);
        saveScrollPosition();
      };
    }
  }, [handleScroll, restoreScrollPosition, saveScrollPosition]);

  // Save position on unmount
  useEffect(() => {
    return () => {
      saveScrollPosition();
    };
  }, [saveScrollPosition]);

  return {
    scrollRef,
    scrollToTop,
    scrollToBottom,
    scrollToElement,
    isAtTop,
    isAtBottom,
    saveScrollPosition,
    restoreScrollPosition
  };
};
